import { combineReducers } from 'redux';
import status, { WebsiteCreationStatus } from './website-creation-status';
import timestamp from './login-link-timestamp';

export interface WebsiteCreation {
  status: WebsiteCreationStatus;
  timestamp: number;
}

export const MAX_RESTART_POLLING_DELTA_IN_MS = 1000 * 60 * 5; // 5 minutes

export const tooOld = (state: WebsiteCreation): boolean => {
  if (!state || !state.timestamp) {
    return false;
  }

  const now = new Date().getTime();
  if (now - state.timestamp > MAX_RESTART_POLLING_DELTA_IN_MS) {
    return true;
  }

  return false;
};

export const shouldPoll = (state: WebsiteCreation): boolean => {
  if (!state) {
    return false;
  }

  if (state.status !== WebsiteCreationStatus.InProgress) {
    return false;
  }

  return Boolean(state.timestamp) && !tooOld(state);
};

export const wasRequested = (state: WebsiteCreation): boolean => {
  if (!state) {
    return false;
  }
  return state.status != null;
};

export const inProgress = (state: WebsiteCreation): boolean => {
  if (!state) {
    return false;
  }
  return WebsiteCreationStatus.InProgress === state.status;
};

export const hasFailed = (state: WebsiteCreation): boolean => {
  if (!state) {
    return false;
  }
  return WebsiteCreationStatus.Failed === state.status;
};

export const isReady = (state: WebsiteCreation): boolean => {
  if (!state) {
    return false;
  }
  return state && WebsiteCreationStatus.Ready === state.status;
};

export const hasFailedOrIsTooOld = (state: WebsiteCreation): boolean => {
  return hasFailed(state) || (inProgress(state) && tooOld(state));
};

export const hasTimestamp = (state: WebsiteCreation): boolean => {
  if (!state) {
    return false;
  }
  return Boolean(state.timestamp);
};

export default combineReducers<WebsiteCreation>({
  status,
  timestamp,
});
